import React from 'react';
import Button from '@mui/material/Button';

import 'components/ResetPasswordForm/ResetPasswordForm.css';
import PasswordInput from 'components/UI/Input/PasswordInput';
import { Form, Link, useActionData, useLoaderData } from 'react-router-dom';
import { LockOutlined } from '@mui/icons-material';
import { Alert, InputAdornment, TextField, Typography } from '@mui/material';
import T from 'i18n-react';
import { changePassword, verifyRecoverToken } from 'lib/recover-password-api';
import validateNewPassword from 'validation/newPasswordValidator';

// Shared, page-styled "log in" action reused by the success and token-expired views.
// Defined at module scope so it is not recreated on every ResetPasswordForm render.
const LoginButton = () => (
  <Button
    className='resetPasswordLoginButton'
    component={Link}
    to={'/login'}
    variant={'contained'}
  >{T.translate('loginComponent.login')}</Button>
);

const ResetPasswordForm = ({ submitting }) => {

  const tokenValid = useLoaderData();
  const actionData = useActionData();

  const changePasswordFailed = actionData?.success === false;
  const changePasswordSucceeded = actionData?.success === true;
  const validationError = actionData?.validationError;

  // A successful change consumes the recovery token server-side, so the router's
  // revalidation re-runs the loader and reports the token as invalid. Check success
  // first so the confirmation screen is shown instead of the token-expired view.
  if (changePasswordSucceeded) {
    return (<>
      <Typography className='infoMessageText' variant={'h6'} component={'h6'}>
        {T.translate('recoverPasswordComponent.passwordChanged')}
      </Typography>
      <LoginButton />
    </>);
  }

  // Render the token-expired view only when the loader reports an invalid token.
  // A failed change (server/token issue at submit time) keeps the form visible so
  // the user can retry, and must NOT link to login (that could imply the password was changed).
  if (!tokenValid) {
    return (<>
      <Typography className='infoMessageText' variant={'h6'} component={'h6'}>
        {T.translate('recoverPasswordComponent.tokenExpired')}
      </Typography>
      <LoginButton />
    </>);
  }

  return (
    <Form className='form' method={'post'}>
      <PasswordInput isError={Boolean(validationError)} />
      <TextField
        name={'confirmPassword'}
        type={'password'}
        placeholder={T.translate('recoverPasswordComponent.confirmPassword')}
        error={Boolean(validationError)}
        InputProps={{
          startAdornment: (
            <InputAdornment position={'start'}>
              <LockOutlined />
            </InputAdornment>
          ),
        }}
      />
      {validationError &&
        <Alert variant='filled' severity='error'>
          {T.translate(validationError)}
        </Alert>
      }
      {changePasswordFailed &&
        <Alert variant='filled' severity='error'>
          {T.translate('recoverPasswordComponent.changePasswordError')}
        </Alert>
      }
      <Button
        className='resetPasswordSubmitButton'
        disabled={submitting}
        variant={'contained'}
        type={'submit'}
      >{T.translate('recoverPasswordComponent.okButton')}</Button>
    </Form>
  );
};

export default ResetPasswordForm;

export const loader = async ({ params }) => {
  return await verifyRecoverToken(params.token);
};

export const action = async ({ request, params }) => {
  const formData = await request.formData();
  const password = formData.get('password');
  const confirmPassword = formData.get('confirmPassword');

  const validationError = validateNewPassword(password, confirmPassword);
  if (validationError) {
    return { validationError };
  }

  const success = await changePassword(password, confirmPassword, params.token);
  return { success };
};
